import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PublicShell from '../components/PublicShell';
import { Home, Shield, ShieldCheck, FileText, BookOpen, Database, LogOut, LogIn, Layers, Cpu, Globe, Zap, DollarSign, Terminal, ChevronDown, ChevronRight, Code2, Palette, Sparkles } from 'lucide-react';
import LeadCaptureCTA from '../components/LeadCaptureCTA';
import useSEO from '../hooks/useSEO';
import {
  LLM_MODELS,
  MASTER_SYSTEM_PROMPT,
  CHUNK_PROMPTS,
  SECTION_PROMPTS,
  AI_STUDIO_PROMPTS,
  ILLUSTRATION_PROMPTS,
  PIPELINE_NOTES,
} from '../data/techStuffPrompts';

const NAV = [
  { path: '/', icon: Home, label: 'Home', color: '#0ea5e9' },
  { path: '/platform', icon: Shield, label: 'Intelligence Platform', auth: true, color: '#16a34a' },
  { path: '/documentation', icon: FileText, label: 'Documentation', color: '#2563eb' },
  { path: '/use-cases', icon: BookOpen, label: 'Use Cases', color: '#9333ea' },
  { path: '/sources', icon: Database, label: 'Research Database', color: '#0891b2' },
  { path: '/provenance', icon: ShieldCheck, label: 'Data Provenance', color: '#059669' },
  { path: '/pricing', icon: DollarSign, label: 'Pricing', color: '#ca8a04' },
];

const SECTIONS = [
  // SCOUT (1-12)
  { num: '01', name: 'Country Snapshot', desc: 'Key indicators, rankings, GDP, population, HDI, credit rating', tier: 'SCOUT' },
  { num: '02', name: 'Executive Summary', desc: 'Country overview, sector status, strategic recommendation', tier: 'SCOUT' },
  { num: '03', name: 'PESTLE Analysis', desc: 'Political, Economic, Social, Technology, Legal, Environmental', tier: 'SCOUT' },
  { num: '04', name: 'Market Opportunity', desc: 'Demand analysis, TAM/SAM/SOM, service gaps', tier: 'SCOUT' },
  { num: '05', name: 'Government Capacity', desc: 'Payment mechanisms, budget, fiscal risk', tier: 'SCOUT' },
  { num: '06', name: 'Competitive Landscape', desc: 'Operators, market structure, entry barriers', tier: 'SCOUT' },
  { num: '07', name: 'Business Model', desc: 'Entry model, revenue streams, phased rollout', tier: 'SCOUT' },
  { num: '08', name: 'Risk Analysis', desc: 'Political, economic, operational risks & mitigation', tier: 'SCOUT' },
  { num: '09', name: 'Financial Projections', desc: 'CAPEX, revenue forecast, IRR, NPV, payback', tier: 'SCOUT' },
  { num: '10', name: 'Sovereign Risk', desc: 'Credit ratings, debt sustainability, macro stability', tier: 'SCOUT' },
  { num: '11', name: 'Social Impact', desc: 'Employment, SDG alignment, ESG scoring', tier: 'SCOUT' },
  { num: '12', name: 'Governance', desc: 'Institutional framework, regulatory quality, PPP readiness', tier: 'SCOUT' },
  // STRATEGIST (13-22)
  { num: '13', name: 'Stakeholder Map', desc: 'Decision-makers, blockers, engagement plan', tier: 'STRATEGIST' },
  { num: '14', name: 'Regulatory Deep Dive', desc: 'Licensing pathway, compliance roadmap', tier: 'STRATEGIST' },
  { num: '15', name: 'Value Chain & Margins', desc: 'Upstream to downstream, profit pool analysis', tier: 'STRATEGIST' },
  { num: '16', name: 'Technology Readiness', desc: 'Infrastructure, connectivity, tech adoption', tier: 'STRATEGIST' },
  { num: '17', name: 'Entry Strategy', desc: 'Entry mode decision tree, 90-day plan', tier: 'STRATEGIST' },
  { num: '18', name: 'Local Partner & JV', desc: 'Partner identification, JV structuring', tier: 'STRATEGIST' },
  { num: '19', name: 'Scenario Planning', desc: 'Bull/Base/Bear with decision gates', tier: 'STRATEGIST' },
  { num: '20', name: 'Tax & Incentives', desc: 'Tax regime, SEZs, DTAAs, structuring', tier: 'STRATEGIST' },
  { num: '21', name: 'Talent & Labor', desc: 'Skill availability, wage benchmarks, unions', tier: 'STRATEGIST' },
  { num: '22', name: 'Supply Chain & Logistics', desc: 'Ports, corridors, localization opportunities', tier: 'STRATEGIST' },
  // SOVEREIGN (23-30)
  { num: '23', name: 'DFI & Blended Finance', desc: 'Active DFI pipeline matched to deal', tier: 'SOVEREIGN' },
  { num: '24', name: 'Deal Structuring & PPP', desc: 'Concession structures, risk allocation', tier: 'SOVEREIGN' },
  { num: '25', name: 'M&A Targets', desc: 'Screened targets with valuations', tier: 'SOVEREIGN' },
  { num: '26', name: 'Geopolitical Brief', desc: 'Conflict, elections, elite dynamics', tier: 'SOVEREIGN' },
  { num: '27', name: 'Diplomatic Relations', desc: 'BITs, trade agreements, dispute history', tier: 'SOVEREIGN' },
  { num: '28', name: 'IC Investment Memo', desc: 'Board-ready go/no-go synthesis', tier: 'SOVEREIGN' },
  { num: '29', name: 'Portfolio Benchmark', desc: 'Cross-country heatmap comparison', tier: 'SOVEREIGN' },
  { num: '30', name: '100-Day Playbook', desc: 'Week-by-week post-commitment roadmap', tier: 'SOVEREIGN' },
];

const TIER_COLOR = { SCOUT: '#004ac6', STRATEGIST: '#00A651', SOVEREIGN: '#7c3aed' };

const AI_STUDIO = [
  { title: 'Section Content Generation', model: 'Intelligence Engine', desc: 'Senior economist persona generates up to 31 sections with chunked Part A + Part B rendering for Strategist/Sovereign tiers. Uses the Pyramid Principle: lead with the answer, MECE logic, quantified assertions. 1,000+ source references.' },
  { title: 'Illustration Generation', model: 'Gemini Nano Banana', desc: 'AI-generated infographics and section illustrations embedded in both the web interface and the investment-grade PDF report. Full-page rendering with auto-rotation for landscape images.' },
  { title: 'Cinematic Brief Generation', model: 'Sora 2 + OpenAI TTS', desc: 'Multi-scene cinematic video briefings — temporarily disabled, planned for the next release. Will be board-ready for investor days, regional strategy sessions, and executive pitches.' },
  { title: 'Audio Narration', model: 'OpenAI TTS', desc: '2-4 minute professional narration of the executive brief — ideal for executives who prefer listening during commutes or travel.' },
  { title: 'Presentation Deck', model: 'Gamma AI', desc: 'Auto-generated slide decks (5-25 slides) with theme picker, language support, and direct link for presentation to stakeholders.' },
];

const PAGES = [
  { title: 'Interactive World Map', desc: 'SVG map (react-simple-maps) with risk-coded markers. Click any country to open its blueprint. Markers are grouped and color-coded by risk level (Stable/Caution/Moderate/High Risk).' },
  { title: 'Intelligence Feed', desc: 'Real-time World Bank data showing GDP Growth, Inflation, Unemployment, FDI, Trade/GDP, External Debt/GNI, and Current Account for all tracked countries. Auto-refreshes on server startup.' },
  { title: 'IMF Economic Forecasts', desc: 'Live IMF World Economic Outlook data for 122 countries showing GDP Growth, Inflation, Unemployment, Current Account, and Government Debt forecasts for 2023-2026.' },
  { title: 'Historical Trend Charts', desc: 'Recharts-powered interactive line charts with 5 economic metrics. Country comparison across 6-year spans with World Bank historical data.' },
  { title: 'Investment-Grade PDF Export', desc: 'ReportLab generates 30-60 page investment-grade PDFs with custom cover page, table of contents, economic trends (KPI table + matplotlib charts), full-page illustrations, and back page branding.' },
  { title: 'Research Database', desc: '1,000+ curated intelligence sources across 20 categories with 145 API integrations. Searchable, filterable, with Source Health Monitor.' },
];

export default function DocumentationPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  useSEO({
    title: 'Documentation — Sovereign IQ | How the AI Country Blueprint Works',
    description: 'How Sovereign IQ generates investment-grade country blueprints: 31 sections, AI Studio, Nano Banana illustrations, Sora 2 cinematic briefs, 1,000+ verified sources and zero-hallucination guardrails.',
    keywords: 'Sovereign IQ documentation, AI country blueprint methodology, 31 section report, BCG Pyramid Principle research, World Bank IMF data integration, Claude Sonnet country intelligence',
    canonical: 'https://sovereigniq.org/documentation',
  });

  const go = (path, auth) => navigate(auth && !user ? '/login' : path);
  const [openPrompt, setOpenPrompt] = useState(null);
  const [openAiStudio, setOpenAiStudio] = useState(null);
  const [openIllust, setOpenIllust] = useState(null);
  const [tierFilter, setTierFilter] = useState('ALL');

  const filteredPrompts = tierFilter === 'ALL'
    ? SECTION_PROMPTS
    : SECTION_PROMPTS.filter(p => p.tier === tierFilter);

  return (
    <PublicShell user={user} onLogout={onLogout}>
      <div className="overflow-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
        {/* Shared dynamic animations */}
        <style>{`
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
          .animate-fadeInUp { animation: fadeInUp 0.5s ease-out forwards; }
          @keyframes slowSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .animate-slowSpin { animation: slowSpin 30s linear infinite; }
          .grid-bg { background-image: linear-gradient(rgba(0,74,198,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,74,198,0.05) 1px, transparent 1px); background-size: 40px 40px; }
          .card-lift { transition: all 0.3s ease; }
          .card-lift:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(11,28,48,0.08); }
        `}</style>

        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#f8f9ff] via-white to-[#eff4ff] border-b border-[#c3c6d7]/20">
          <div className="absolute inset-0 grid-bg opacity-60" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-gradient-to-br from-[#004ac6]/5 via-[#7c3aed]/5 to-transparent animate-slowSpin pointer-events-none" />
          <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
            <div className="inline-flex items-center gap-2 bg-white border border-[#c3c6d7]/40 rounded-full px-3 py-1 mb-5 shadow-sm animate-fadeInUp">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#434655]">Documentation · Platform reference</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-[#0b1c30] leading-[1.05] mb-4 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
              Platform <span className="bg-gradient-to-r from-[#004ac6] to-[#7c3aed] bg-clip-text text-transparent">Documentation</span>
            </h1>
            <p className="text-base text-[#434655] leading-relaxed max-w-2xl animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              Complete technical overview of the Sovereign IQ intelligence engine — architecture, AI models, data sources, and blueprint taxonomy across Scout, Strategist, and Sovereign tiers.
            </p>
          </div>
        </section>

        <div className="p-6 lg:p-8 max-w-[1100px]">

          {/* What It Is */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 card-lift">
            <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight mb-3">What Is Sovereign IQ?</h2>
            <p className="text-xs text-[#434655] leading-relaxed mb-3">
              <strong>Sovereign IQ</strong> is an AI-powered country intelligence and sovereign risk assessment platform. It generates investment-grade country blueprints across 115+ industry sectors, combining real-time World Bank and IMF economic data with institutional-grade AI analysis powered by our AI intelligence engine.
            </p>
            <p className="text-xs text-[#434655] leading-relaxed">
              The platform is designed for investment professionals, development finance institutions, multinational corporations, consulting firms, and government agencies evaluating market entry into emerging and frontier economies.
            </p>
          </div>

          {/* Architecture */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6 card-lift">
            <div className="px-5 py-4 bg-gradient-to-r from-[#0b1c30] via-[#1a365d] to-[#004ac6] text-white flex justify-between items-center">
              <div><h4 className="text-xs font-bold uppercase tracking-widest">System Architecture v2.0</h4><p className="text-[10px] text-white/50">Full-Stack Intelligence Infrastructure</p></div>
              <Layers className="w-5 h-5 text-white/40" />
            </div>
            <div className="divide-y divide-[#c3c6d7]/20">
              {[
                { title: 'Frontend', desc: 'React 18, Tailwind CSS, Shadcn/UI, Recharts, react-simple-maps, i18n (EN/FR/PT)', badge: 'PRODUCTION' },
                { title: 'Backend', desc: 'Python FastAPI, Motor (async MongoDB), httpx, ThreadPoolExecutor for non-blocking LLM calls', badge: 'PRODUCTION' },
                { title: 'AI Engine', desc: 'AI engine (text, chunked), Gemini Nano Banana (images), Sora 2 (cinematic brief video), OpenAI TTS (audio), Gamma (decks)', badge: 'ACTIVE' },
                { title: 'Live Data', desc: 'World Bank API (8 indicators), IMF WEO (5 forecasts for 122 countries), auto-refreshing', badge: 'REAL-TIME' },
                { title: 'PDF Engine', desc: 'ReportLab — investment-grade format with AI Executive Brief, inline charts, Unicode-safe text, custom cover + back page branding', badge: 'PRODUCTION' },
                { title: 'Research Database', desc: '1,000+ curated sources across 20 categories with 145 API integrations. Dynamic source injection into AI prompts.', badge: '1000 SOURCES' },
                { title: 'Authentication', desc: 'JWT with bcrypt password hashing + Admin-approval workflow. Public pages accessible without login.', badge: 'JWT + APPROVAL' },
                { title: 'Database', desc: 'MongoDB via Motor async driver. Collections: users, countries (blueprints with 31-section content), research_sources, generation_logs, leads, report_versions, observations (Data Fabric audit trail).', badge: 'MONGODB' },
              ].map((r, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-4 hover:bg-[#f8f9ff] transition-all">
                  <div><h5 className="text-sm font-bold text-[#0b1c30]">{r.title}</h5><p className="text-xs text-[#434655]">{r.desc}</p></div>
                  <span className="text-[9px] font-bold text-[#004ac6] bg-[#e5eeff] px-2 py-0.5 rounded-full shrink-0 ml-4">{r.badge}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pages & Features */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight mb-4 flex items-center gap-2"><Globe className="w-4 h-4 text-[#004ac6]" /> Pages & Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {PAGES.map((p, i) => (
                <div key={i} className="p-4 bg-[#f8f9ff] rounded-lg">
                  <h4 className="text-xs font-bold text-[#0b1c30] mb-1">{p.title}</h4>
                  <p className="text-[11px] text-[#434655] leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 30 Blueprint Sections */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 card-lift">
            <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight mb-4 flex items-center gap-2"><FileText className="w-4 h-4 text-[#004ac6]" /> 30 Blueprint Sections — Three Tiers</h2>
            <p className="text-xs text-[#434655] mb-4">Each section is generated independently by <strong>our AI intelligence engine</strong> with a senior economist persona, referencing 1,000+ authoritative data sources. Strategist and Sovereign tiers use chunked Part&nbsp;A + Part&nbsp;B generation for 2× richer content (~10-12k chars per section).</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {SECTIONS.map(s => (
                <div key={s.num} className="bg-[#f8f9ff] rounded-lg p-4" style={{ borderLeft: `3px solid ${TIER_COLOR[s.tier]}` }}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black text-[#c3c6d7]">{s.num}</span>
                    <span className="text-[8px] font-black uppercase tracking-widest" style={{ color: TIER_COLOR[s.tier] }}>{s.tier}</span>
                  </div>
                  <h4 className="text-sm font-bold text-[#0b1c30] mt-1">{s.name}</h4>
                  <p className="text-[10px] text-[#434655] mt-1 leading-tight">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* AI Studio */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden card-lift">
            <div className="px-5 py-4 bg-gradient-to-r from-[#0b1c30] via-[#1a365d] to-[#7c3aed] text-white flex justify-between items-center">
              <div><h4 className="text-xs font-bold uppercase tracking-widest">AI Studio Capabilities</h4><p className="text-[10px] text-white/50">Multimodal Intelligence Generation</p></div>
              <Cpu className="w-5 h-5 text-white/40" />
            </div>
            <div className="divide-y divide-[#c3c6d7]/20">
              {AI_STUDIO.map((c, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-4 hover:bg-[#f8f9ff] transition-all">
                  <div className="flex-1"><h5 className="text-sm font-bold text-[#0b1c30]">{c.title}</h5><p className="text-xs text-[#434655]">{c.desc}</p></div>
                  <span className="text-[9px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full shrink-0 ml-4">{c.model}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ─────────── TECH STUFF ─────────── */}
          <div className="mt-8 mb-6">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden card-lift">
              <div className="px-5 py-4 bg-gradient-to-r from-[#0b1c30] via-[#1a1a2e] to-[#2e1065] text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Terminal className="w-5 h-5 text-white/70" />
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-widest">TechStuff</h4>
                      <p className="text-[10px] text-white/60">Full transparency — every LLM, model ID & prompt used across the platform</p>
                    </div>
                  </div>
                  <Sparkles className="w-5 h-5 text-white/40" />
                </div>
              </div>
              <div className="p-6 space-y-6">

                {/* LLM Models Matrix */}
                <div>
                  <h3 className="text-sm font-bold text-[#0b1c30] mb-3 flex items-center gap-2"><Cpu className="w-4 h-4 text-[#7c3aed]" /> LLM Models & Providers</h3>
                  <div className="space-y-2">
                    {LLM_MODELS.map((m) => (
                      <div key={m.id} className="bg-[#f8f9ff] rounded-lg p-4 border-l-[3px]" style={{ borderLeftColor: m.name.includes('Claude') ? '#f59e0b' : m.name.includes('Gemini') ? '#10b981' : m.name.includes('OpenAI TTS') ? '#3b82f6' : '#7c3aed' }}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-bold text-[#0b1c30]">{m.name}</p>
                          <code className="text-[10px] font-mono bg-white px-2 py-0.5 rounded text-[#7c3aed] border border-[#c3c6d7]/40">{m.id}</code>
                        </div>
                        <p className="text-[11px] text-[#434655] mb-1"><span className="font-semibold">Provider:</span> {m.provider}</p>
                        <p className="text-[11px] text-[#434655] mb-1"><span className="font-semibold">Used for:</span> {m.used_for}</p>
                        <p className="text-[11px] text-[#434655]"><span className="font-semibold">Pricing:</span> {m.pricing} · <span className="font-semibold">Streaming:</span> {m.streaming}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Master System Prompt */}
                <div>
                  <h3 className="text-sm font-bold text-[#0b1c30] mb-3 flex items-center gap-2"><Code2 className="w-4 h-4 text-[#004ac6]" /> Master System Prompt (applied to every section)</h3>
                  <pre className="bg-[#0b1c30] text-[#c4b5fd] p-4 rounded-lg text-[10.5px] leading-relaxed overflow-x-auto font-mono whitespace-pre-wrap max-h-[260px] overflow-y-auto border border-[#7c3aed]/30">{MASTER_SYSTEM_PROMPT}</pre>
                </div>

                {/* Chunked Generation (Part A + Part B) */}
                <div>
                  <h3 className="text-sm font-bold text-[#0b1c30] mb-3 flex items-center gap-2"><Layers className="w-4 h-4 text-[#004ac6]" /> Chunked Generation · Part A + Part B</h3>
                  <p className="text-[11px] text-[#434655] mb-3">Every section runs through two streaming LLM calls (plus a self-heal pass). Combined output ~3,000-3,500 words. Each chunk is clamped to ~800 words to finish under the 60s proxy ceiling.</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] font-bold text-[#7c3aed] mb-1">PART A PROMPT (first half, max 800w)</p>
                      <pre className="bg-[#0b1c30] text-[#c4b5fd] p-3 rounded-lg text-[10px] leading-relaxed overflow-x-auto font-mono whitespace-pre-wrap max-h-[180px] overflow-y-auto">{CHUNK_PROMPTS.partA}</pre>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-[#7c3aed] mb-1">PART B PROMPT (second half + KEY TAKEAWAYS + sentinel)</p>
                      <pre className="bg-[#0b1c30] text-[#c4b5fd] p-3 rounded-lg text-[10px] leading-relaxed overflow-x-auto font-mono whitespace-pre-wrap max-h-[180px] overflow-y-auto">{CHUNK_PROMPTS.partB}</pre>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-[10px] font-bold text-[#f59e0b] mb-1">SELF-HEAL COMPLETION CALL (triggered if sentinel missing)</p>
                    <pre className="bg-[#0b1c30] text-[#fcd34d] p-3 rounded-lg text-[10px] leading-relaxed overflow-x-auto font-mono whitespace-pre-wrap max-h-[140px] overflow-y-auto">{CHUNK_PROMPTS.selfHealCompletion}</pre>
                  </div>
                </div>

                {/* Pipeline Notes */}
                <div>
                  <h3 className="text-sm font-bold text-[#0b1c30] mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-[#f59e0b]" /> Pipeline Guardrails & Architecture</h3>
                  <div className="grid md:grid-cols-2 gap-2">
                    {PIPELINE_NOTES.map((n, i) => (
                      <div key={i} className="bg-[#f8f9ff] rounded-lg p-3">
                        <p className="text-[11px] font-bold text-[#0b1c30]">{n.label}</p>
                        <p className="text-[10.5px] text-[#434655] leading-relaxed mt-1">{n.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 30 Section Prompts (collapsible) */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-[#0b1c30] flex items-center gap-2"><FileText className="w-4 h-4 text-[#004ac6]" /> 30 Section Prompts</h3>
                    <div className="flex items-center gap-1" data-testid="tier-filter">
                      {['ALL', 'SCOUT', 'STRATEGIST', 'SOVEREIGN'].map(t => (
                        <button key={t} onClick={() => setTierFilter(t)}
                          data-testid={`tier-filter-${t.toLowerCase()}`}
                          className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full transition-all ${tierFilter === t ? 'text-white' : 'text-[#434655] bg-[#eff4ff] hover:bg-[#e5eeff]'}`}
                          style={{ backgroundColor: tierFilter === t ? (t === 'SCOUT' ? '#004ac6' : t === 'STRATEGIST' ? '#00A651' : t === 'SOVEREIGN' ? '#7c3aed' : '#0b1c30') : undefined }}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5" data-testid="section-prompts-list">
                    {filteredPrompts.map((p) => {
                      const open = openPrompt === p.key;
                      return (
                        <div key={p.key} className="bg-white border border-[#c3c6d7]/30 rounded-lg overflow-hidden">
                          <button
                            onClick={() => setOpenPrompt(open ? null : p.key)}
                            data-testid={`prompt-row-${p.key}`}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#f8f9ff] transition-all">
                            {open ? <ChevronDown className="w-4 h-4 text-[#737686] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#737686] shrink-0" />}
                            <span className="text-[10px] font-black text-[#c3c6d7] shrink-0">#{String(p.num).padStart(2, '0')}</span>
                            <span className="text-[9px] font-black uppercase tracking-widest shrink-0 px-1.5 py-0.5 rounded" style={{ color: TIER_COLOR[p.tier], backgroundColor: `${TIER_COLOR[p.tier]}15` }}>{p.tier}</span>
                            <span className="text-xs font-bold text-[#0b1c30] flex-1">{p.title}</span>
                            <span className="text-[10px] text-[#737686] italic truncate hidden md:block max-w-[40%]">{p.focus}</span>
                          </button>
                          {open && (
                            <div className="border-t border-[#c3c6d7]/30 bg-[#0b1c30] p-4">
                              <pre className="text-[#c4b5fd] text-[10.5px] leading-relaxed font-mono whitespace-pre-wrap max-h-[420px] overflow-y-auto">{p.prompt}</pre>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* AI Studio Inline Prompts */}
                <div>
                  <h3 className="text-sm font-bold text-[#0b1c30] mb-3 flex items-center gap-2"><Cpu className="w-4 h-4 text-[#7c3aed]" /> AI Studio · Inline Prompts</h3>
                  <div className="space-y-1.5" data-testid="ai-studio-prompts-list">
                    {AI_STUDIO_PROMPTS.map((a, idx) => {
                      const open = openAiStudio === idx;
                      return (
                        <div key={idx} className="bg-white border border-[#c3c6d7]/30 rounded-lg overflow-hidden">
                          <button
                            onClick={() => setOpenAiStudio(open ? null : idx)}
                            data-testid={`ai-studio-row-${idx}`}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#f8f9ff] transition-all">
                            {open ? <ChevronDown className="w-4 h-4 text-[#737686] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#737686] shrink-0" />}
                            <span className="text-xs font-bold text-[#0b1c30] flex-1">{a.label}</span>
                            <span className="text-[9px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full shrink-0">{a.model}</span>
                          </button>
                          {open && (
                            <div className="border-t border-[#c3c6d7]/30 bg-[#0b1c30] p-4 space-y-3">
                              {a.system && (
                                <div>
                                  <p className="text-[10px] font-bold text-[#fbbf24] mb-1">SYSTEM MESSAGE</p>
                                  <pre className="text-[#fde68a] text-[10px] leading-relaxed font-mono whitespace-pre-wrap">{a.system}</pre>
                                </div>
                              )}
                              <div>
                                <p className="text-[10px] font-bold text-[#a78bfa] mb-1">USER PROMPT</p>
                                <pre className="text-[#c4b5fd] text-[10.5px] leading-relaxed font-mono whitespace-pre-wrap max-h-[360px] overflow-y-auto">{a.prompt}</pre>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Illustration Prompts */}
                <div>
                  <h3 className="text-sm font-bold text-[#0b1c30] mb-3 flex items-center gap-2"><Palette className="w-4 h-4 text-[#10b981]" /> Illustration Prompts (Gemini + Claude HTML fallback)</h3>
                  <p className="text-[11px] text-[#434655] mb-3">Admin can toggle between Gemini Nano Banana (direct image gen) and Claude Sonnet 4.6 HTML→PNG (headless Chromium render) from the User Management page. Both paths store identical <code className="bg-[#eff4ff] px-1 rounded text-[#7c3aed]">countries.section_illustrations.&lt;key&gt;</code> shape.</p>
                  <div className="space-y-1.5" data-testid="illustration-prompts-list">
                    {ILLUSTRATION_PROMPTS.map((il, idx) => {
                      const open = openIllust === idx;
                      return (
                        <div key={idx} className="bg-white border border-[#c3c6d7]/30 rounded-lg overflow-hidden">
                          <button
                            onClick={() => setOpenIllust(open ? null : idx)}
                            data-testid={`illust-row-${idx}`}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#f8f9ff] transition-all">
                            {open ? <ChevronDown className="w-4 h-4 text-[#737686] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#737686] shrink-0" />}
                            <span className="text-xs font-bold text-[#0b1c30] flex-1">{il.label}</span>
                            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0">{il.model}</span>
                          </button>
                          {open && (
                            <div className="border-t border-[#c3c6d7]/30 bg-[#064e3b] p-4 space-y-3">
                              {il.system && (
                                <div>
                                  <p className="text-[10px] font-bold text-[#fbbf24] mb-1">SYSTEM MESSAGE</p>
                                  <pre className="text-[#fde68a] text-[10px] leading-relaxed font-mono whitespace-pre-wrap">{il.system}</pre>
                                </div>
                              )}
                              <div>
                                <p className="text-[10px] font-bold text-[#6ee7b7] mb-1">PROMPT</p>
                                <pre className="text-[#a7f3d0] text-[10.5px] leading-relaxed font-mono whitespace-pre-wrap max-h-[360px] overflow-y-auto">{il.prompt}</pre>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <LeadCaptureCTA source_page="documentation" headline="Ready to see Sovereign IQ in action?" subline="Schedule a live walkthrough or get a sample 31-section blueprint tailored to your target country & sector." />
        </div>
      </div>
    </PublicShell>
  );
}
